import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { GridRowEditStopReasons, type GridRowEditStopParams, type GridRowModel, type MuiEvent, type useGridApiRef } from '@mui/x-data-grid-premium';
import { isAddRowShortcut } from '@/lib/gridShortcuts';
import { changedEditRowIds } from '@/lib/pendingRowEdits';

type GridApiRef = ReturnType<typeof useGridApiRef>;
export type EditRows = Record<string, Record<string, { value?: unknown }>>;

export interface InlineRow {
  id: string;
}

/**
 * The row a commit writes, from the row with its edited values: what the row's cells stashed for the commit merged
 * in (a lookup's hidden id, a computed default's override flag) and its derived fields computed again. A promise
 * when the row needs the server first; the outside-click commit then leaves the row to the grid's own commit —
 * never a half-completed row into the host — and both commits ask, so an async completeRow must answer the same
 * row's question with one request (the in-flight map in ContractVolumeBasesTab's resolver is the precedent).
 */
export type CompleteRow<T> = (row: T) => T | Promise<T>;

export interface InlineRowEditingOptions<T extends InlineRow> {
  apiRef: GridApiRef;
  data: T[];
  onChange: (rows: T[]) => void;
  /** Whether a row still being edited holds a changed value — the host counts it as unsaved. */
  onPendingEditChange?: (pending: boolean) => void;
  /** The new row New and the add-row shortcut put first, from the rows as they are (for a next line number). */
  newRow: (rows: T[]) => T;
  completeRow?: CompleteRow<T>;
  /** The field a new row opens on; none only scrolls to it. */
  focusField?: string | null;
  editMode?: 'row' | 'cell';
  /** After a row was added, with its id (a detail panel follows it). */
  onRowAdded?: (id: string) => void;
}

/**
 * The editing of an inline child grid, shared by every generated one and the hand-written grids that edit like them:
 *
 * - **Commit before an outside click.** A mousedown outside the grid (Save, say) writes every row still in edit
 *   mode into the host before the click runs, so the host acts on what was typed.
 * - **Unsaved while typed.** A row in edit mode holding a changed value is reported (`typedRowIds`, to tint it, and
 *   `onPendingEditChange`, to enable Save) — one check drives both.
 * - **⌘⏎ / Ctrl+⏎ adds a row.** The row being typed is committed and a new one opened, first in the grid.
 * - **Tab out of the last cell** moves focus to New (`addButtonRef`), so the next line is one keypress away.
 * - **Enter on a select's option** picks it and keeps the row in edit mode, so Tab goes on to the next cell.
 * - **A new row opens in edit mode** on `focusField`, once the grid has the row.
 *
 * What is the entity's own goes in: its new row, how a committed row is completed, the field a new row opens on.
 */
export function useInlineRowEditing<T extends InlineRow>(options: InlineRowEditingOptions<T>) {
  const { apiRef, data, onChange, newRow, completeRow, focusField, editMode = 'row', onRowAdded } = options;
  // Refs, so the document listener and the shortcut, built once, see the latest rows and callbacks.
  const dataRef = useRef(data);
  dataRef.current = data;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const completeRowRef = useRef(completeRow);
  completeRowRef.current = completeRow;
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const focusNewRow = useFocusNewRow(apiRef, data, focusField, editMode);

  const handleAdd = () => {
    // dataRef, not the data prop: the shortcut path flushes the row being typed into the
    // parent first, and the closure's data prop would still be the pre-flush array.
    const row = newRow(dataRef.current);
    // A new row goes first: until the save it stays at the top, where it was typed, instead of
    // landing below every saved row. (A paged grid pins it there from its change set anyway.)
    onChangeRef.current([row, ...dataRef.current]);
    onRowAdded?.(row.id);
    focusNewRow(row.id);
  };

  // Completions still waiting for the server: while one is in flight the host must keep counting
  // the row as unsaved — its values are not in the parent yet, so a save made now misses them and
  // the form must not look clean.
  const [inFlightCompletions, setInFlightCompletions] = useState(0);

  const processRowUpdate = async (updatedRow: GridRowModel): Promise<GridRowModel> => {
    const completed = (completeRowRef.current ?? identity)(updatedRow as T);
    const row = completed instanceof Promise ? await trackCompletion(completed, setInFlightCompletions) : completed;
    // flushSync, so a click handler that runs next (Save) reads the committed row.
    flushSync(() => onChangeRef.current(replaceRow(dataRef.current, row)));
    return row;
  };

  // Flush every row still in edit mode into the parent, reading the DataGrid's edit state
  // synchronously. Callers need the parent to hold current values BEFORE they act: the
  // outside-click handler (so Save sees what was typed) and the add-row shortcut
  // (which appends to the parent's array and would otherwise drop the row being typed).
  const commitEditingRows = useCallback(() => {
    const api = apiRef.current;
    const editRows = (api?.state as { editRows?: EditRows } | undefined)?.editRows;
    if (!api || !editRows) return;
    for (const [rowId, cells] of Object.entries(editRows)) {
      if (!cells || Object.keys(cells).length === 0) continue;
      flushEditedRow(rowId, cells, dataRef, onChangeRef.current, completeRowRef.current);
      api.stopRowEditMode({ id: rowId });
    }
  }, [apiRef]);

  useCommitOnOutsideMouseDown(apiRef, commitEditingRows);
  const typedRowIds = useTypedRowIds(apiRef, dataRef);
  // Unsaved while typed OR while a commit's server completion is on its way into the parent.
  usePendingEditNotification(typedRowIds.size > 0 || inFlightCompletions > 0, options.onPendingEditChange);

  const handleGridKeyDown = (e: React.KeyboardEvent) => {
    if (!isAddRowShortcut(e)) return;
    e.preventDefault();
    commitEditingRows();
    handleAdd();
  };

  // Tab past the last editable cell is the user saying "this row is done". The grid
  // would leave focus on the cell that just closed; move it to New instead, so adding
  // the next line is one more keypress (and Shift+Tab out the front is left alone).
  // An Enter that picks an option in an open select is not "done": the menu sits in a portal, but the key bubbles
  // through the React tree to the cell, and the grid would end the row — Tab would then leave the grid.
  const handleRowEditStop = (params: GridRowEditStopParams, event?: MuiEvent) => {
    if (params.reason === GridRowEditStopReasons.enterKeyDown && event && picksAnOption(event)) {
      event.defaultMuiPrevented = true;
      return;
    }
    if (params.reason !== GridRowEditStopReasons.tabKeyDown) return;
    // After the row commit settles — stopRowEditMode restores focus to the cell.
    setTimeout(() => addButtonRef.current?.focus(), 0);
  };

  return { handleAdd, processRowUpdate, commitEditingRows, typedRowIds, handleGridKeyDown, handleRowEditStop, addButtonRef, dataRef, onChangeRef };
}

const identity = <T,>(row: T) => row;

/** Whether a key came from an option list — a select's menu — rather than from the cell's own input. */
function picksAnOption(event: MuiEvent): boolean {
  const target = (event as { target?: unknown }).target;
  return target instanceof Element && target.closest('[role="listbox"], [role="menu"]') !== null;
}

function replaceRow<T extends InlineRow>(rows: T[], row: T): T[] {
  return rows.map((r) => (String(r.id) === String(row.id) ? row : r));
}

/** The promise with the in-flight counter held while it runs, so the host counts the row unsaved meanwhile. */
function trackCompletion<T>(completed: Promise<T>, setInFlight: (update: (n: number) => number) => void): Promise<T> {
  setInFlight((n) => n + 1);
  return completed.finally(() => setInFlight((n) => n - 1));
}

/** Reports the pending flag to the host when it changes, and clears it on unmount. */
export function usePendingEditNotification(pending: boolean, onPendingEditChange: ((pending: boolean) => void) | undefined) {
  const onChangeRef = useRef(onPendingEditChange);
  onChangeRef.current = onPendingEditChange;
  const reported = useRef(false);
  useEffect(() => {
    if (pending === reported.current) return;
    reported.current = pending;
    onChangeRef.current?.(pending);
  }, [pending]);
  useEffect(() => () => {
    if (reported.current) onChangeRef.current?.(false);
  }, []);
}

/** One row in edit mode written into the parent with its edited values, unless completing it needs the server. */
function flushEditedRow<T extends InlineRow>(
  rowId: string,
  cells: Record<string, { value?: unknown }>,
  dataRef: { current: T[] },
  onChange: (rows: T[]) => void,
  completeRow: CompleteRow<T> | undefined,
): void {
  const existing = dataRef.current.find((r) => String(r.id) === rowId);
  if (!existing) return;
  const edited = Object.fromEntries(Object.entries(cells).map(([field, cell]) => [field, cell?.value]));
  const row = (completeRow ?? identity)({ ...existing, ...edited });
  if (row instanceof Promise) {
    // The grid's own commit, which stopRowEditMode starts next, completes the row again and reports a failure.
    row.catch(() => undefined);
    return;
  }
  const updated = replaceRow(dataRef.current, row);
  flushSync(() => onChange(updated));
  dataRef.current = updated;
}

/**
 * Scrolls to a row handleAdd appended and opens it in edit mode once the grid knows about it. Driven by state and an
 * effect: the effect runs after React committed the row and the grid ingested its rows prop, so the row exists — no
 * timers, no race. Returns the setter.
 */
function useFocusNewRow(apiRef: GridApiRef, data: unknown[], focusField: string | null | undefined, editMode: 'row' | 'cell') {
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingFocusId) return;
    const api = apiRef.current;
    if (!api || !api.getRowNode(pendingFocusId)) return;
    const rowIndex = api.getRowIndexRelativeToVisibleRows(pendingFocusId);
    if (rowIndex != null && rowIndex >= 0) api.scrollToIndexes({ rowIndex });
    const field = shownFocusField(api, focusField);
    if (field && editMode === 'row') api.startRowEditMode({ id: pendingFocusId, fieldToFocus: field });
    if (field && editMode === 'cell') api.startCellEditMode({ id: pendingFocusId, field });
    setPendingFocusId(null);
  }, [pendingFocusId, data, apiRef, focusField, editMode]);
  return setPendingFocusId;
}

/**
 * The field a new row opens on: the focus field while the grid shows its column, else the first editable column it
 * shows. A generated grid leaves out the columns its parent record rules out (visibleWhen.parentField) — a calculation
 * price list has no product column — and focusing a column the grid lacks throws inside the grid.
 */
function shownFocusField(api: NonNullable<GridApiRef['current']>, focusField: string | null | undefined): string | null {
  if (!focusField) return null;
  const shown = api.getVisibleColumns();
  return shown.some((column) => column.field === focusField) ? focusField : shown.find((column) => column.editable)?.field ?? null;
}

/**
 * Commits every editing row when the user presses the mouse outside the grid (the Save button, say), BEFORE that
 * click's handler runs. A press inside a floating layer above the page — an autocomplete's list, a select's menu,
 * a dialog stacked over the grid — is not outside; the modal the grid itself lives in (a grid hosted in a dialog)
 * is no such layer, so that dialog's own Save still commits.
 */
export function useCommitOnOutsideMouseDown(apiRef: GridApiRef, commitEditingRows: () => void) {
  useEffect(() => {
    const handleExternalMouseDown = (e: MouseEvent) => {
      const gridRoot = apiRef.current?.rootElementRef?.current;
      if (!gridRoot || gridRoot.contains(e.target as Node)) return;
      const layer = (e.target as HTMLElement).closest?.('.MuiAutocomplete-popper, .MuiModal-root, .MuiPopper-root');
      if (layer && !layer.contains(gridRoot)) return;
      commitEditingRows();
    };
    document.addEventListener('mousedown', handleExternalMouseDown);
    return () => document.removeEventListener('mousedown', handleExternalMouseDown);
  }, [apiRef, commitEditingRows]);
}

/**
 * The rows in edit mode that hold a changed value. A row being typed counts as unsaved before it commits: it is
 * tinted like a saved-later row and the host enables Save on the first changed value, while the outside mousedown
 * commits every open row before Save's click runs. Exported for grids that edit by cell outside the hook
 * (the price-list matrix); row-editing grids get it through the hook.
 */
export function useTypedRowIds(apiRef: GridApiRef, dataRef: { current: readonly { id: unknown }[] }) {
  const [typedRowIds, setTypedRowIds] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    let typedKey = '';
    const unsubscribe = api.subscribeEvent('stateChange', () => {
      const ids = changedEditRowIds((api.state as { editRows?: EditRows }).editRows, dataRef.current as unknown as Record<string, unknown>[]);
      const key = [...ids].sort().join(',');
      if (key !== typedKey) {
        typedKey = key;
        setTypedRowIds(ids);
      }
    });
    return () => {
      unsubscribe();
      setTypedRowIds(new Set());
    };
  }, [apiRef, dataRef]);
  return typedRowIds;
}
